import React, { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import { useNotifications } from '../../contexts/NotificationContext';
import { SystemAlertBanner } from '../ui/Toast';
import { CommandPalette } from '../ui/CommandPalette';
import { CommandPaletteProvider } from '../../contexts/CommandPaletteContext';
import { cn } from '../../utils/cn';
import { useAppSelector } from '../../store';
import { useServiceWorkerNavigation } from '../../hooks/useServiceWorkerNavigation';
import { InstallAppBanner } from '../pwa/InstallAppBanner';
import { DesktopNotificationsAlertBar, PushNotificationsProvider } from '../pwa/DesktopNotifications';
import { useIsMobileNav } from '../../hooks/useMediaQuery';

const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed';

const readStoredCollapsed = (): boolean => {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
};

const Layout: React.FC = () => {
  const { systemAlert, dismissSystemAlert } = useNotifications();
  useServiceWorkerNavigation();

  // Get lastPermissionUpdate to force Sidebar re-render when permissions change
  const lastPermissionUpdate = useAppSelector((state) => state.auth.lastPermissionUpdate);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(readStoredCollapsed);

  // Mobile drawer open state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isMobileNav = useIsMobileNav();
  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);

  // Close mobile drawer on route change so tapping a nav link dismisses it
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // A drawer left open while the window grows to desktop width would keep its
  // body scroll lock (and, before the CSS guard, a click-blocking overlay).
  useEffect(() => {
    if (!isMobileNav) setMobileMenuOpen(false);
  }, [isMobileNav]);

  // Lock body scroll while the mobile drawer is open
  useEffect(() => {
    if (mobileMenuOpen) {
      const previous = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = previous; };
    }
  }, [mobileMenuOpen]);

  return (
    <CommandPaletteProvider>
      <PushNotificationsProvider>
        <div className="app-layout bg-slate-50">
          {/* Key forces Sidebar to re-render when permissions change */}
          <Sidebar
            key={lastPermissionUpdate}
            collapsed={sidebarCollapsed}
            onToggleCollapse={setSidebarCollapsed}
            mobileOpen={mobileMenuOpen}
            onMobileClose={closeMobileMenu}
          />
          <div className={cn('main-container', sidebarCollapsed && 'main-container-collapsed')}>
            <Header onOpenMobileMenu={() => setMobileMenuOpen(true)} />
            <main className="main-content">
              {/* Inline alerts: only critical / personal-action ones, slim (h-10) bars. */}
              {systemAlert && (
                <div className="mb-4">
                  <SystemAlertBanner alert={systemAlert} onDismiss={dismissSystemAlert} />
                </div>
              )}
              <div className="mb-4 empty:hidden lg:hidden">
                <InstallAppBanner />
              </div>
              <div className="mb-4 empty:hidden">
                <DesktopNotificationsAlertBar />
              </div>
              <Outlet />
            </main>
          </div>
          {/* Global Command Palette */}
          <CommandPalette />
        </div>
      </PushNotificationsProvider>
    </CommandPaletteProvider>
  );
};

export default Layout;
